import {
  Button,
  Flex,
  Group,
  Progress,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import {
  deleteContact,
  deleteFilterContacts,
  downloadFilterList,
  editContact,
  findContact,
  findList,
  getFirstPageContacts,
  getUniqueValues,
  searchInContactlist,
} from "../../../../../api/ContactAPI";
import MainTable from "../../../../../layouts/Table/MainTable";
import ItemDeleter from "../../../../../components/TableComponents/ItemDeleter";
import { useContactHandler } from "../../components/useContactHandler";
import { IconArrowBarUp } from "@tabler/icons-react";
import { getChangedKeys, getListOrderName } from "../../listUtils";
import { notifications } from "@mantine/notifications";
import Jobs from "../../components/jobs/Jobs";
import { Link, useParams } from "react-router-dom";
import AIActions from "../../components/AIActions";
import TableFilterExport from "../../../../../components/TableComponents/TableFilterExport";
import TableFilterDelete from "../../../../../components/TableComponents/TableFilterDelete";
import TableFilterCopyContacts from "../../components/copy/TableFilterCopyContacts";
import DiffShow from "../../../../../components/View/DiffShow";
import ColumnAdder from "./ColumnAdder";
import ListName from "./ListName";
import ContactPreview from "./ContactPreview";
import NoItemsModal from "../../../../../components/TableComponents/NoItemsModal";
import { getListOrigin } from "../listing";
import { useListStore } from "../../../../../stores/ListStore";
import ListNumbers from "./ListNumbers";
import GenerateDatafieldContent from "../../actions/GenerateDatafieldContent";
import ListLanguage from "../../components/ListLanguage";
import ListCountry from "../../components/ListCountry";
import dateColumn from "../../../../../layouts/Table/columns/dateColumn";
import TableFilterToUnsub from "../../components/toUnsub/TableFilterToUnsub";
import TableFilterToBlacklist from "../../components/toBlacklist/TableFilterToBlacklist";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";
import TableFilterEnrichMobile from "../../components/TableFilterEnrichMobile";
import UndoubleList from "../../components/unDouble/UndoubleList";
import CronSettings from "../../components/crons/CronSettings";
import ContactCreate from "../../components/ContactCreate";
import { getPermission } from "../../../../../functions/utilFunctions";
import ListEnrich from "../../actions/ListEnrich";
import ContactsExteriorFilters from "../../components/ContactsExteriorFilters";
import { updateSelf } from "../../../../../api/UserAPI";
import { useConfigStore } from "../../../../../stores/ConfigStore";
import HeaderRenderer from "../../components/HeaderRenderer";
import EditColumnRows from "../../actions/EditColumnRows";
import SaveListColumnsById from "../../components/SaveListColumnsById";
import ListUrlUsed from "../../actions/ListUrlUsed";

const fieldsWithCustomOptions = [
  "%activity%",
  "%company_size%",
  "%legal_form%",
  "%naf_code%",
  "%legal_nature%",
];

const ListsContent = ({ isMinimized }) => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();
  const { contactListId } = useParams();
  const screenIsLarge = window.innerWidth > 1800;
  const [reloadColumns, setReloadColumns] = useState(true);
  const [needReload, setNeedReload] = useState(false);
  const currentList = useListStore((s) => s.currentList);
  const setCurrentList = useListStore((s) => s.setCurrentList);
  const loadProfile = useListStore((s) => s.loadProfile);
  const setLoadProfile = useListStore((s) => s.setLoadProfile);
  const [selectedContact, setSelectedContact] = useState(null);

  const customFieldHeader = useCallback(
    (datafield) => (
      <Group wrap="nowrap" gap={4} className="hover-source">
        <GenerateDatafieldContent
          current={datafield}
          onFinish={() => setLoadProfile(true)}
        />
        <Text>{datafield?.name}</Text>{" "}
        <EditColumnRows
          current={datafield}
          total={currentList?.number_of_contacts}
        />
      </Group>
    ),
    [currentList?.number_of_contacts],
  );

  const headerRenderer = useCallback(
    (datafield) => (
      <HeaderRenderer
        datafield={datafield}
        total={currentList?.number_of_contacts}
      />
    ),
    [currentList?.number_of_contacts],
  );

  const handleNewField = useCallback(() => {
    setReloadColumns(true);
  }, []);

  const additionalColumns = useMemo(
    () => [
      {
        field: "bounced",
        id: "bounced",
        headerName: lang.contact_lists.bounced,
        type: "exist",
        filterable: true,
        sortable: false,
        width: 75,
        renderCell: ({ row, value }) => {
          return "-";
        },
      },
    ],
    [],
  );

  const startingColumns = useMemo(
    () => [
      numberColumn(),
      dateColumn({
        filterable: true,
        others: {
          type: "date",
          valueGetter: (params) => new Date(params.value || ""),
        },
      }),
    ],
    [],
  );

  const {
    loading,
    columns,
    rows,
    rowCount,
    serverInfo,
    handleLoading,
    onFilterChange,
    onPaginationChange,
    onSortChange,
  } = useContactHandler({
    apiRef,
    listId: contactListId,
    getFirstPage: getFirstPageContacts,
    searchFunction: searchInContactlist,
    // additionalMainRequest: () => setLoadProfile(true),
    disableQueryTransform: true,
    cellEditable: true,
    disableEdit: true,
    pageOrderName: "contact_list_fetch_contacts_order",
    reloadColumns,
    setReloadColumns,
    additionalColumns,
    onNameClick: setSelectedContact,
    startingColumns,
    getFieldDetails: (datafield) => {
      if (fieldsWithCustomOptions.includes(datafield?.identifier_placeholder)) {
        return {
          option_name: datafield.name,
          type: "lazy",
          id: datafield.id.toString(),
          options: [],
          filterable: true,
          field: datafield.id.toString(),
          getter: () =>
            getUniqueValues({
              contact_list_id: contactListId,
              datafield_id: datafield.id,
            }),
          responseField: "results",
          storeKey: "customOptions",
          setStoreKey: "setCustomOptions",
          handleResponse: (result) => {
            if (result) {
              return result.map((x) => ({
                id: x,
                name: x,
                option_name: x,
              }));
            }
            return [];
          },
          conditionType: "boolean",
          free: true,
        };
      }
      return {};
    },
    headerRendererCustomField: customFieldHeader,
    renderAllHeader: headerRenderer,
    additionalFields: { bounced: null },
    tableDefaultSize: 30,
  });

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <ItemDeleter
          title_bold={`${row[4]} ${row[3]}`}
          handleDelete={deleteContact}
          deletedMessage={lang.contact_lists.content.delete_success}
          parentId={currentList?.id}
          id={row.id}
          apiRef={apiRef}
        />
      );
    },
    [lang.contact_lists.content.delete_success, currentList?.id, apiRef],
  );

  const toolbarActions = useCallback(
    ({ selectedIds, filter }) => (
      <>
        {getPermission("displayContactListExport") && (
          <TableFilterExport
            id={currentList?.id}
            selectedIds={selectedIds}
            filter={filter}
            handleExport={downloadFilterList}
            name={currentList?.name}
            filterResultNumber={rowCount}
            total={currentList?.number_of_contacts}
          />
        )}
        <TableFilterCopyContacts
          id={currentList?.id}
          selectedIds={selectedIds}
          filter={filter}
          name={currentList?.name}
          filterResultNumber={rowCount}
          total={currentList?.number_of_contacts}
          onFinish={() => {
            setLoadProfile(true);
            useListStore.getState().setListingLoad(true);
          }}
        />
        <ListCountry
          current={currentList}
          onFinish={() => {
            setLoadProfile(true);
          }}
        />
        <ListLanguage
          current={currentList}
          onFinish={() => {
            setLoadProfile(true);
          }}
        />
        {getPermission("findMobilePhone") && (
          <TableFilterEnrichMobile
            id={currentList?.id}
            selectedIds={selectedIds}
            filter={filter}
            filterResultNumber={rowCount}
            total={currentList?.number_of_contacts}
            onFinish={() => setLoadProfile(true)}
          />
        )}
        <ListEnrich
          current={currentList}
          inTable
          selectedIds={selectedIds}
          filter={filter}
          filterResultNumber={rowCount}
          total={currentList?.number_of_contacts}
          onFinish={() => {
            setLoadProfile(true);
            useListStore.getState().setListingLoad(true);
          }}
        />

        <ListUrlUsed
          current={currentList}
          onFinish={() => {
            setLoadProfile(true);
            useListStore.getState().setListingLoad(true);
          }}
          inList
        />
        <TableFilterToUnsub
          id={currentList?.id}
          selectedIds={selectedIds}
          filter={filter}
          name={currentList?.name}
          filterResultNumber={rowCount}
          total={currentList?.number_of_contacts}
          onFinish={() => setLoadProfile(true)}
        />
        <TableFilterToBlacklist
          id={currentList?.id}
          selectedIds={selectedIds}
          filter={filter}
          name={currentList?.name}
          filterResultNumber={rowCount}
          total={currentList?.number_of_contacts}
          onFinish={() => setLoadProfile(true)}
        />
        <UndoubleList
          id={currentList?.id}
          onFinish={() => {
            setLoadProfile(true);
            useListStore.getState().setListingLoad(true);
          }}
        />
        <TableFilterDelete
          id={currentList?.id}
          selectedIds={selectedIds}
          filter={filter}
          handleDelete={deleteFilterContacts}
          apiRef={apiRef}
          onFinish={(type) => {
            handleLoading(type !== "selection");
            setLoadProfile(true);
          }}
          filterResultNumber={rowCount}
          total={currentList?.number_of_contacts}
        />
      </>
    ),
    [apiRef, currentList, handleLoading, rowCount, setLoadProfile],
  );

  useEffect(() => {
    if (loadProfile) {
      updateSelf(true);
      findList(contactListId)
        .then((response) => {
          setCurrentList(response.data.contact_list_profile);
        })
        .finally(() => setLoadProfile(false));
    }
  }, [contactListId, loadProfile]);

  const handleRowUpdate = useCallback(async (updatedRow, originalRow) => {
    const changedKey = getChangedKeys(updatedRow, originalRow);
    if (updatedRow[changedKey] !== originalRow[changedKey]) {
      let rowElement = document.querySelector(
        `[role="rowgroup"] [data-id="${originalRow.id}"]`,
      );

      rowElement.style.opacity = 0.3;

      editContact({
        contact_id: originalRow?.id,
        list_id: originalRow?.contact_list_id,
        properties: [
          {
            data_field_id: parseInt(changedKey),
            value: updatedRow[changedKey],
          },
        ],
      })
        .then(() => {
          findContact(originalRow.contact_list_id, originalRow.id)
            .then((r) => {
              let newProfile = r.data.contact_profile;
              let result = {
                id: newProfile.id,
                ...newProfile,
              };
              const datafields = useConfigStore.getState().datafields;
              newProfile.properties.forEach((prop) => {
                let field = datafields.find((x) => x.id === prop.data_field_id);

                if (field) result[field.id] = prop.value;
              });

              apiRef.current.updateRows([result]);
            })
            .finally(() => {
              rowElement.style.opacity = 1;
            });
        })
        .catch((err) => {
          rowElement.style.opacity = 1;
          let cellElement = document.querySelector(
            `[role="rowgroup"] [data-id="${originalRow.id}"] > [data-field="${changedKey}"]`,
          );
          let linkElement = document.querySelector(
            `[role="rowgroup"] [data-id="${originalRow.id}"] > [data-field="${changedKey}"] a`,
          );
          let e = linkElement || cellElement;

          e.innerText = originalRow?.[changedKey] || "-";

          apiRef.current.updateRows([originalRow]);
        });

      return updatedRow;
    }

    return originalRow;
  }, []);

  const handleRowUpdateError = useCallback((err) => {
    return notifications.show({ message: "Contact non modifié", color: "red" });
  }, []);

  const getColumnActions = useCallback(
    (handleClose) => (
      <Stack gap={4} mt={8} w={"100%"}>
        <SaveListColumnsById
          handleClose={handleClose}
          list={currentList}
          apiRef={apiRef}
        />
      </Stack>
    ),
    [currentList, apiRef],
  );

  useEffect(() => {
    if (
      rows.length === 0 &&
      currentList?.state_details?.find((x) => x.state === "Completed")
    ) {
      let refresher = document.getElementById("refresher");

      if (refresher) {
        refresher.click();
      }
    }
  }, [currentList?.state_details, rows]);

  if (!columns || !currentList) {
    return;
  }

  return (
    <Stack h={"100%"} flex={1} pr={16} gap={8} pt={12} miw={1024}>
      <Group mt={4}>
        <Text fw={700} fz={24}>
          {lang.prospects.my_contacts}
        </Text>

        <CronSettings />
        <AIActions
          handleNewField={handleNewField}
          onFinish={() => setLoadProfile(true)}
          currentList={currentList}
          isMinimized={screenIsLarge ? true : isMinimized}
        />
        <ContactCreate
          onCreated={handleLoading}
          isMinimized={screenIsLarge ? true : isMinimized}
        />
        <Button
          leftSection={<IconArrowBarUp size={24} fill="#fff" stroke={0} />}
          fz={16}
          ml={8}
          radius={"md"}
          size="md"
          className="main-list-action"
          px={24}
          variant="outline"
          component={Link}
          to={`/contact-lists/${currentList?.id}/import`}
        >
          {lang.prospects.import}
        </Button>
      </Group>

      <Group mb={isMinimized ? 24 : 0} mt={0}>
        <ThemeIcon variant="subtle" mr={-8}>
          {getListOrigin(currentList)}
        </ThemeIcon>

        <ListName
          currentList={currentList}
          onRenamed={() => setLoadProfile(true)}
        />

        <ListNumbers currentList={currentList} serverInfo={serverInfo} />
      </Group>

      <Group
        pl={8}
        justify="end"
        mr={58}
        gap={24}
        pos={"relative"}
        style={{ zIndex: 33 }}
        pr={20}
        mb={-62}
        h={32}
        ml={525}
        wrap="nowrap"
      >
        <ContactsExteriorFilters
          apiRef={apiRef}
          loading={loading}
          screenIsLarge={screenIsLarge}
        />
        <DiffShow
          handleLoading={handleLoading}
          show={needReload}
          setShow={setNeedReload}
        />

        <Jobs
          row={currentList}
          setCurrentList={setCurrentList}
          isContent={true}
          onDiff={() => setNeedReload(true)}
          inColumn={false}
          isList
          setLoading={setLoadProfile}
        />
      </Group>

      <Flex h={0} flex={1} mt={10} pos={"relative"}>
        <ContactPreview
          contact={selectedContact}
          setContact={setSelectedContact}
        />
        <NoItemsModal
          rows={rows}
          totalRows={currentList?.number_of_contacts || 0}
          loading={loading}
          apiRef={apiRef}
          text={
            currentList?.state_details?.find(
              (x) => x.state === "InQueue" || x.state === "InProgress",
            ) ? (
              <Stack>
                <Progress size="xl" value={100} animated />

                <Text size="xl">{lang.prospects.importing_contact}</Text>
              </Stack>
            ) : (
              <Text size="xl" mb={18}>
                {lang.prospects.no_contact}
              </Text>
            )
          }
          mt={16}
          button={
            !currentList?.state_details?.find(
              (x) => x.state === "InQueue" || x.state === "InProgress",
            ) && (
              <Button
                leftSection={
                  <IconArrowBarUp size={24} fill="#fff" stroke={0} />
                }
                fz={16}
                ml={8}
                radius={"md"}
                size="md"
                className="main-list-action"
                px={24}
                component={Link}
                to={`/contact-lists/${currentList?.id}/import`}
              >
                {lang.prospects.import}
              </Button>
            )
          }
        />

        <MainTable
          isClient={false}
          apiRef={apiRef}
          columns={columns}
          rows={rows}
          rowCount={rowCount}
          loading={loading}
          setLoading={() => {
            handleLoading();
            setLoadProfile(true);
            setNeedReload(false);
          }}
          customColumnsButton={
            <ColumnAdder
              handleColumnCreated={() => {
                setReloadColumns(true);
              }}
            />
          }
          id={"list-content-table"}
          listId={currentList?.id}
          allow200PerPage
          defaultPerPage={30}
          hasFolder
          toolbarActions={toolbarActions}
          pageName={"contact_lists_contacts"}
          pageOrderName={getListOrderName(currentList)}
          // pageOrderName={"contact_list_fetch_contacts_order"}
          onPaginationChange={onPaginationChange}
          actionsCell={actionsCell}
          onFilterChange={onFilterChange}
          onSortChange={onSortChange}
          actionsCellWidth={50}
          withBorderTop
          whiteButtons
          getColumnActions={getColumnActions}
          handleRowUpdate={handleRowUpdate}
          handleRowUpdateError={handleRowUpdateError}
          additionalFilter={[
            {
              field_name: "bounced",
              label: "Bounce",
              option_name: "Bounce",
              name: "Bounce",
              id: "bounced",
              type: "exist",
              filterable: true,
              sortable: false,
              getOptions: () => [
                { id: "true", name: "Oui" },
                { id: "false", name: "Non" },
              ],
            },
          ]}
          tableSx={{
            overflow: "hidden",
            "& .MuiDataGrid-pinnedColumns": {
              background: "#fff",
              borderTopLeftRadius: 3,
              border: "none",
            },
            "& .MuiDataGrid-pinnedColumnHeaders": {
              background: "#fff",
              border: "none",
            },
            "& .table-row.Mui-hovered": {
              // background: "var(--mantine-color-primary-2) !important",
              color: "black !important",

              "& a": {
                color: "black !important",
              },
            },
            "& .table-search": {
              width: isMinimized ? "160px !important" : "150px !important",
            },
            "& .table-toolbar": {
              zIndex: 16,
            },
            "& .table-columns-button": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          }}
        />
      </Flex>
    </Stack>
  );
};

export default ListsContent;
